import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { type Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { type PaginatedResponse } from '../../../shared/interfaces/paginated-response.interface';
import { type Product } from './product.interface';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly http = inject(HttpClient);

  readonly products = signal<Product[]>([]);
  readonly isLoading = signal(false);
  readonly isLoadingMore = signal(false);
  readonly error = signal<string | null>(null);
  readonly currentPage = signal(1);
  readonly totalPages = signal(1);
  readonly hasMorePages = signal(true);
  readonly totalItems = signal(0);

  // Maintain active search context for pagination
  readonly currentSearchTerm = signal('');
  readonly currentCategoryId = signal<number | null>(null);
  readonly currentSortBy = signal<string>('default');
  readonly currentOnlyInStock = signal<boolean>(false);

  loadProducts(
    searchTerms = '',
    categoryId: number | null = null,
    sortBy: string | null = null,
    onlyInStock = false,
    page = 1,
    limit = 10,
  ): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.currentSearchTerm.set(searchTerms);
    this.currentCategoryId.set(categoryId);
    this.currentSortBy.set(sortBy || 'default');
    this.currentOnlyInStock.set(onlyInStock);

    let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    if (searchTerms) {
      params = params.set('term', searchTerms);
    }
    if (categoryId) {
      params = params.set('categoryId', categoryId.toString());
    }
    if (sortBy || this.currentSortBy()) {
      params = params.set('sortBy', sortBy || this.currentSortBy());
    }
    if (onlyInStock || this.currentOnlyInStock()) {
      params = params.set('onlyInStock', 'true');
    }

    this.http.get<PaginatedResponse<Product>>(`${environment.apiUrl}/v1/store-products/search`, { params }).subscribe({
      next: (response) => {
        this.products.set(response.data);
        this.currentPage.set(response.page);
        this.totalPages.set(response.totalPages);
        this.totalItems.set(response.total);
        this.hasMorePages.set(response.page < response.totalPages);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.error.set(error.message);
        this.isLoading.set(false);
      },
    });
  }

  loadMoreProducts(limit = 10): void {
    if (this.isLoadingMore() || !this.hasMorePages()) {
      return;
    }

    const nextPage = this.currentPage() + 1;
    this.isLoadingMore.set(true);

    let params = new HttpParams().set('page', nextPage.toString()).set('limit', limit.toString());

    if (this.currentSearchTerm()) {
      params = params.set('term', this.currentSearchTerm());
    }
    if (this.currentCategoryId()) {
      params = params.set('categoryId', this.currentCategoryId()!.toString());
    }
    if (this.currentSortBy()) {
      params = params.set('sortBy', this.currentSortBy());
    }
    if (this.currentOnlyInStock()) {
      params = params.set('onlyInStock', 'true');
    }

    this.http.get<PaginatedResponse<Product>>(`${environment.apiUrl}/v1/store-products/search`, { params }).subscribe({
      next: (response) => {
        const currentProducts = this.products();
        this.products.set([...currentProducts, ...response.data]);
        this.currentPage.set(response.page);
        this.totalPages.set(response.totalPages);
        this.totalItems.set(response.total);
        this.hasMorePages.set(response.page < response.totalPages);
        this.isLoadingMore.set(false);
      },
      error: (error) => {
        this.error.set(error.message);
        this.isLoadingMore.set(false);
      },
    });
  }

  getFeaturedProducts(limit = 4): Observable<PaginatedResponse<Product>> {
    return this.http.get<PaginatedResponse<Product>>(`${environment.apiUrl}/v1/store-products/search`, {
      params: {
        featured: 'true',
        limit: limit.toString(),
      },
    });
  }

  getBestsellers(limit = 8): Observable<PaginatedResponse<Product>> {
    return this.http.get<PaginatedResponse<Product>>(`${environment.apiUrl}/v1/store-products/bestsellers`, {
      params: {
        limit: limit.toString(),
      },
    });
  }

  getNewArrivals(limit = 8): Observable<PaginatedResponse<Product>> {
    return this.http.get<PaginatedResponse<Product>>(`${environment.apiUrl}/v1/store-products/new-arrivals`, {
      params: {
        limit: limit.toString(),
      },
    });
  }

  resetPagination(): void {
    this.currentPage.set(1);
    this.totalPages.set(1);
    this.totalItems.set(0);
    this.hasMorePages.set(true);
    this.products.set([]);
    this.currentSearchTerm.set('');
    this.currentCategoryId.set(null);
    this.currentSortBy.set('default');
    this.currentOnlyInStock.set(false);
  }

  getProductById(id: string): Observable<Product> {
    return this.http.get<Product>(`${environment.apiUrl}/v1/store-products/${id}`);
  }
}
